import React, { useEffect, useState } from 'react';
import azureDevOpsService from './services/azureDevOpsService';
import { getSprintPeriods, getCurrentSprintIndex } from './utils/sprintUtils';
import { Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const STATUS_COLORS = [
  'rgba(59,130,246,0.7)',
  'rgba(234,179,8,0.7)',
  'rgba(34,197,94,0.7)',
  'rgba(239,68,68,0.7)',
  'rgba(212, 248, 52, 0.7)',
  'rgba(236,72,153,0.7)',
  'rgba(168,85,247,0.7)',
];

export default function KpiBugsBySprintStatus() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      const sprintStart = new Date('2025-11-05T00:00:00');
      const sprintStartNumber = 23;
      const currentSprintIndex = getCurrentSprintIndex(sprintStart);
      const firstSprintIndex = Math.max(0, currentSprintIndex - 7);
      const sprints = getSprintPeriods(sprintStart, firstSprintIndex, 8, sprintStartNumber);
      const bugs = await azureDevOpsService.getBugsWithTA();
      
      // Filtrer pour exclure les bugs résolus (Done, Cancelled, Validation)
      const unresolvedBugs = bugs.filter(bug => {
        const state = bug.state;
        return state !== '6- Done' && state !== '6- Cancelled' && state !== '5- Validation';
      });
      
      // Date de début du premier sprint à afficher
      const firstSprintStart = sprints[0].start;
      
      // Séparer les bugs anciens (avant les 8 derniers sprints) et les bugs des 8 derniers sprints
      const oldBugs = unresolvedBugs.filter(bug => {
        const created = new Date(bug.creationDate);
        return created < firstSprintStart;
      });
      
      const recentBugs = unresolvedBugs.filter(bug => {
        const created = new Date(bug.creationDate);
        return created >= firstSprintStart;
      });
      
      // Détecter tous les statuts uniques présents dans les bugs non résolus
      const uniqueStatuses = [...new Set(unresolvedBugs.map(bug => bug.state).filter(Boolean))];
      
      // Trier les statuts par ordre croissant (1- New, 2- Active, 3- Test, etc.)
      uniqueStatuses.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      
      // Compter les bugs anciens par statut
      const oldBugsStatusCounts = {};
      uniqueStatuses.forEach(status => {
        oldBugsStatusCounts[status] = oldBugs.filter(bug => bug.state === status).length;
      });
      
      // Grouper bugs par sprint et statut
      const sprintStatusData = sprints.map(sprint => {
        const bugsInSprint = recentBugs.filter(bug => {
          const created = new Date(bug.creationDate);
          return created >= sprint.start && created <= sprint.end;
        });
        // Compter par statut
        const statusCounts = {};
        uniqueStatuses.forEach(status => {
          statusCounts[status] = bugsInSprint.filter(bug => bug.state === status).length;
        });
        return statusCounts;
      });
      
      // Ajouter les bugs anciens au début
      sprintStatusData.unshift(oldBugsStatusCounts);
      
      // Préparer les datasets pour le graphique empilé
      const datasets = uniqueStatuses.map((status, idx) => ({
        label: status,
        data: sprintStatusData.map(row => row[status]),
        backgroundColor: STATUS_COLORS[idx % STATUS_COLORS.length],
        stack: 'bugs',
      }));
      
      setChartData({
        labels: ['Avant', ...sprints.map(s => s.name)],
        datasets,
      });
      setLoading(false);
    }
    fetchData();
  }, []);

  return (
    <div className="bg-white rounded shadow p-6">
      <h2 className="text-lg font-bold mb-4">Bugs TA non résolus par sprint et statut</h2>
      {loading || !chartData ? (
        <div>Chargement...</div>
      ) : (
        <Bar
          data={chartData}
          options={{
            responsive: true,
            plugins: {
              legend: { position: 'top' },
              title: {
                display: true,
                text: 'Bugs TA par sprint (statut)',
              },
            },
            scales: {
              x: { stacked: true, title: { display: true, text: 'Sprint' } },
              y: { stacked: true, title: { display: true, text: 'Nbr de bugs' }, beginAtZero: true },
            },
          }}
        />
      )}
    </div>
  );
}
